import { ReactiveParam, reactiveParam } from '@/utils/reactive-param'
import { init } from 'echarts'
import { reactive, shallowRef, unref, watchEffect } from 'vue'
import { tryOnScopeDispose, useElementSize } from '@vueuse/core'
import theme from './theme'
import type { ECharts, EChartsOption } from 'echarts'

/**
 * Create a chart instance.
 */
export function useChart(
    el: ReactiveParam<HTMLElement | undefined>,
    option?: ReactiveParam<EChartsOption | undefined>,
    initOptions?: Parameters<typeof init>[2]
) {
    /**
     * ECharts instance.
     */
    const instance = shallowRef<ECharts>()

    /**
     * Reactive chart options.
     */
    const optionRef = reactiveParam(option)

    /**
     * Reactive container.
     */
    const elRef = reactiveParam(el)

    /**
     * Reactive container size.
     */
    const size = reactive(useElementSize(elRef))

    /**
     * Translations.
     */
    const { t } = useI18n()

    /**
     * Whether the chart is currently resizing.
     */
    let isResizing = false

    /**
     * Update the chart options.
     */
    function setOption() {
        const chart = unref(instance)

        if (!chart || !chart.getWidth() || !chart.getHeight()) return

        const option = unref(optionRef)

        if (!option) return

        const update = () => {
            chart.setOption(option, {
                notMerge: true,
                lazyUpdate: true
            })
        }

        if (isResizing) {
            void onFinished(update)
        } else {
            update()
        }
    }

    /**
     * Resize the chart.
     */
    function resize() {
        const chart = unref(instance)
        if (!chart) return

        if (size.height && size.width) {
            isResizing = true
            chart.resize({ ...size, animation: { duration: 150 } })
            void onFinished(() => (isResizing = false))
        }
    }

    /**
     * Dispose the chart.
     */
    function dispose() {
        unref(instance)?.dispose()
    }

    /**
     * Initialize the chart instance.
     */
    function initialize() {
        const element = unref(elRef)

        if (!element && !initOptions?.ssr) return

        dispose()

        instance.value = init(element as HTMLElement, theme(t), initOptions)
    }

    /**
     * Returen a promise that resolves when the chart emits the given event.
     */
    function promisifyListener(eventName: string, cb: () => void) {
        const chart = unref(instance)

        if (!chart) return

        const once = (resolve: () => void) => {
            const _cb = () => {
                cb()
                chart.off(eventName, _cb)
                resolve()
            }

            return _cb
        }

        return new Promise<void>(resolve => {
            chart.on(eventName, once(resolve))
        })
    }

    /**
     * Return a promise that resolves when the chart is rendered.
     */
    function onRendered(cb: () => void) {
        return promisifyListener('rendered', cb)
    }

    /**
     * Return a promise that resolves when the chart is finished.
     */
    function onFinished(cb: () => void) {
        return promisifyListener('finished', cb)
    }

    watchEffect(initialize)
    watchEffect(resize)
    watchEffect(setOption)
    tryOnScopeDispose(dispose)

    return {
        instance,
        dispose,
        resize,
        size,
        onRendered,
        onFinished,
        option: optionRef,
        el: elRef
    }
}
